import { Box, ButtonBase, Typography, useTheme } from '@mui/material';
import React from 'react';

import { Sparkline } from '../../../components/Sparkline.tsx';
import { marketService } from '../../../services/marketService.js';
import { mockIndices, type MarketIndexSnapshot } from '../../market/mockData.js';
import type { WidgetProps } from '../types/index.js';
import { HorizontalPager } from './components/HorizontalPager.tsx';
import { useProfileNavigation } from '../hooks/useProfileNavigation.js';

const SPARKLINE_WIDTH = 140;
const SPARKLINE_HEIGHT = 40;

const formatNumber = (value: number) => new Intl.NumberFormat(undefined, { maximumFractionDigits: 2 }).format(value);
const formatPercent = (value: number) => `${value >= 0 ? '+' : ''}${value.toFixed(2)}%`;

// Sparkline path construction replaced by shared <Sparkline> component

export const IndicesOverviewWidget: React.FC<WidgetProps> = ({ widget, onUpdateOutputs, onUpdateState }) => {
  const theme = useTheme();
  const [indices, setIndices] = React.useState<MarketIndexSnapshot[]>(() => mockIndices);
  const { openProfileByTicker } = useProfileNavigation();

  const createSymbolClickHandler = React.useCallback(
    (symbol: string) => (event: React.MouseEvent<HTMLButtonElement>) => {
      event.stopPropagation();
      openProfileByTicker(symbol);
    },
    [openProfileByTicker]
  );

  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await marketService.getIndices().catch(() => []);
        if (!cancelled && Array.isArray(data) && data.length > 0) {
          // Trust backend shape; cast into MarketIndexSnapshot minimal fields used here
          const mapped = data.map((d: any) => ({
            id: d.id,
            name: d.name,
            symbol: d.symbol,
            level: Number(d.level),
            changePercent: Number(d.changePercent),
            intradaySparkline: Array.isArray(d.intradaySparkline) ? d.intradaySparkline : [],
          })) as MarketIndexSnapshot[];
          setIndices(mapped);
          onUpdateState({ loading: false, data: mapped });
          return;
        }
      } catch {}
      if (!cancelled) {
        setIndices(mockIndices);
        onUpdateState({ loading: false, data: mockIndices });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const selectedOutput = React.useMemo(() => {
    return widget.outputs.find((output) => output.id === 'selectedIndex')?.value as MarketIndexSnapshot | undefined;
  }, [widget.outputs]);

  const [selectedId, setSelectedId] = React.useState<string | null>(selectedOutput?.id ?? null);

  React.useEffect(() => {
    onUpdateState({ loading: false, data: indices });
  }, [indices, onUpdateState]);

  React.useEffect(() => {
    if (!selectedOutput && indices.length > 0) {
      const first = indices[0];
      if (selectedId !== first.id) {
        setSelectedId(first.id);
        onUpdateOutputs({ selectedIndex: first });
      }
    }
  }, [indices, onUpdateOutputs, selectedOutput, selectedId]);

  React.useEffect(() => {
    if (selectedOutput && selectedOutput.id !== selectedId) {
      setSelectedId(selectedOutput.id);
    }
  }, [selectedOutput, selectedId]);

  const handleSelect = React.useCallback(
    (snapshot: MarketIndexSnapshot) => {
      setSelectedId(snapshot.id);
      onUpdateOutputs({ selectedIndex: snapshot });
    },
    [onUpdateOutputs]
  );

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Remove internal duplicate title/subtitle to save space */}
      <HorizontalPager ariaLabel="Indices overview scroller">
        {indices.map((snapshot) => {
          const selected = snapshot.id === selectedId;
          const changePositive = snapshot.changePercent >= 0;
          return (
            <Box
              key={snapshot.id}
              onClick={() => handleSelect(snapshot)}
              sx={{
                width: 160,
                minWidth: 150,
                height: 150,
                borderRadius: 1,
                border: '1px solid',
                borderColor: selected ? 'primary.main' : 'divider',
                backgroundColor: selected ? 'action.selected' : 'background.paper',
                p: 1,
                cursor: 'pointer',
                display: 'grid',
                gridTemplateRows: 'auto 1fr auto',
                overflow: 'hidden',
                transition: 'border-color 0.2s ease, background-color 0.2s ease',
                '&:hover': {
                  borderColor: selected ? 'primary.light' : theme.palette.primary.light,
                },
                scrollSnapAlign: 'start',
              }}
            >
              <Box sx={{ minHeight: 58 }}>
                <Typography
                  variant="subtitle2"
                  sx={{
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden',
                  }}
                >
                  {snapshot.name}
                </Typography>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  noWrap
                  sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
                >
                  <ButtonBase
                    component="span"
                    disableRipple
                    onClick={createSymbolClickHandler(snapshot.symbol)}
                    sx={{
                      font: 'inherit',
                      color: 'inherit',
                      px: 0.25,
                      mx: -0.25,
                      borderRadius: 1,
                      textTransform: 'uppercase',
                      '&:hover': { color: 'primary.main' },
                      '&:focus-visible': {
                        outline: '2px solid',
                        outlineColor: 'primary.main',
                        outlineOffset: 2,
                      },
                    }}
                  >
                    {snapshot.symbol}
                  </ButtonBase>
                  • {formatNumber(snapshot.level)}
                </Typography>
                <Typography
                  variant="body2"
                  color={changePositive ? 'success.main' : 'error.main'}
                  sx={{ fontWeight: 600, mt: 0.5 }}
                >
                  {formatPercent(snapshot.changePercent)}
                </Typography>
              </Box>
              <Sparkline
                data={Array.isArray(snapshot.intradaySparkline) ? snapshot.intradaySparkline : []}
                color={changePositive ? theme.palette.success.main : theme.palette.error.main}
                width={SPARKLINE_WIDTH}
                height={SPARKLINE_HEIGHT}
              />
            </Box>
          );
        })}
      </HorizontalPager>
    </Box>
  );
};
